import debounce from 'debounce';
import React, { useMemo, useState } from 'react';
import { Size } from '../../common/size';
import { Variant } from '../../common/variant';
import { useTestId } from '../../hooks/useTestId';
import { Text } from '../../Typography/Text/Text';
import { Input, NumericInput, PasswordInput } from '../Input/Input';

const DEFAULT_DEBOUNCE_MS = 500;

export interface IFormControlValidationResult {
  valid: boolean;
  message?: string;
}

export enum ValidatorEvent {
  OnBlur = 'onBlur',
  OnChange = 'onChange'
}

interface IValidateOnProps {
  events: ValidatorEvent[];
  debouncedEvents?: ValidatorEvent[];
  debouncedMs?: number;
}

interface IBaseValidatorProps {
  onValidated: (result: IFormControlValidationResult) => void;
  validateOn?: IValidateOnProps;
}

export interface IFormControlValidatorProps extends IBaseValidatorProps {
  onValidate: (value: string) => IFormControlValidationResult;
}

export interface IFormControlRegexValidatorProps extends IBaseValidatorProps {
  regex: RegExp;
  message: string;
}

export const FormControlValidator: React.FC<IFormControlValidatorProps> = ({
  onValidate,
  onValidated,
  validateOn = { events: [ValidatorEvent.OnBlur] },
  children,
  ...rest
}) => {
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const { generateTestId } = useTestId(rest, 'FormControlValidator');

  const handleValidation = (fieldValue: string) => {
    const { valid, message } = onValidate(fieldValue);

    setIsValid(valid);
    setValidationMessage(message || '');
    onValidated({ valid, message });
  };

  const debouncedHandleValidation = useMemo(() => debounce(handleValidation, validateOn.debouncedMs ?? DEFAULT_DEBOUNCE_MS), [validateOn.debouncedMs]);

  const buildChildEventProps = (child: any) => {
    const props: Record<string, (e: { target: { value: string } }) => void> = {};

    validateOn.events.forEach((eventName) => {
      props[eventName] = (e) => {
        if (child.props[eventName]) {
          child.props[eventName](e);
        }

        if (validateOn.debouncedEvents?.includes(eventName)) {
          debouncedHandleValidation(e.target.value);
        } else {
          handleValidation(e.target.value);
        }
      };
    });

    return props;
  };

  const cloneChildren = () => {
    return React.Children.map(children, (child: any) => {
      if (![Input.displayName, PasswordInput.displayName, NumericInput.displayName].includes(child.type.displayName)) {
        return child;
      }

      return React.cloneElement(child, {
        ...child.props,
        invalid: !isValid,
        ...buildChildEventProps(child)
      });
    });
  };

  return (
    <>
      {cloneChildren()}
      {validationMessage && (
        <Text size={Size.Small} variant={Variant.Error} {...generateTestId('Text')}>
          {validationMessage}
        </Text>
      )}
    </>
  );
};

export const FormControlRegexValidator: React.FC<IFormControlRegexValidatorProps> = ({ regex, message, onValidated, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'FormControlRegexValidator');
  const validateRegex = (value: string) => {
    const valid = !regex || regex.test(value);

    return { valid, message: valid ? '' : message };
  };

  return <FormControlValidator onValidate={validateRegex} onValidated={onValidated} {...rest} {...generateTestId()} />;
};
